import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NavigateFunction } from "react-router-dom";

import { Chip } from "@material-tailwind/react";
import { format } from "date-fns";

import {
	archiveDeleteEligibility,
	CampaignSummary,
	TicketLinkedEntityType,
} from "@signalist/shared-types";

import { ReactComponent as ThreeDots } from "@assets/icons/three-dots.svg";
import SquareCheck from "@assets/icons/list-builder/square-check.svg?url";
import Square from "@assets/icons/list-builder/square.svg?url";

import { PermissionRoles } from "@/enum";

import { ListProcessingStatus, ListStatus } from "@/enum/list.enum";
import { CampaignStatus } from "@/enum/campaign-status.enum";

import { Checkbox } from "@/components/Checkbox/Checkbox";

import { TableColumn } from "@/components/Table/Table";

import { translateRequestError } from "@/utils/errors";

import { classNames } from "@/utils/classNames";
import { useRunListNowMutation } from "@/pages/Private/redux/list/list.api";

import { MenuItem } from "@/components/ContextMenu/ContextMenu";
import { ReportProblemMenu } from "@/components/SupportTicket/ReportProblemMenu";

import { SvgIcon } from "@/components/Icon/SvgIcon";

import { buildCampaignBuilderPath } from "../utils/campaignBuilderPath";
import { DownloadFile, List } from "../schema/list";
import { EnrichmentCell, ListNameCell } from "./listTableStatusCells";
import {
	CampaignMetricSubCell,
	CampaignNameSubCell,
	CampaignsBulkCell,
	CampaignStatusSubCell,
} from "./ListCampaignSubRows";

interface UseListTableColumnsParams {
	notify: (message: string) => void;
	selectAll: boolean;
	toggleSelectAll: () => void;
	selectedFiles: DownloadFile[];
	handleSelectFile: (file: DownloadFile) => void;
	sortState: { sortBy: string; sortOrder: number };
	handleToggle: (id: number, status: ListStatus) => Promise<void>;
	isSequencerActive: boolean;
	isBulkPending: (id: number) => boolean;
	getSummaries: (id: number) => CampaignSummary[] | undefined;
	bulkToggle: (id: number, next: CampaignStatus) => void;
	toggleCampaign: (parentId: number, campaign: CampaignSummary) => void;
	navigate: NavigateFunction;
	handleDownloadSelected: (file?: DownloadFile[]) => void;
	handleResetModal: (row: List) => void;
	handleArchiveModal: (row: List) => void;
	handleDeleteModal: (row: List) => void;
	handleDuplicateModal: (row: List) => void;
}

/**
 * Builds the Lead List overview table column definitions. Extracted from {@link ListTable} to
 * keep that component focused (and under the file-length cap); the column render/order logic is a
 * single cohesive responsibility that takes the row handlers + selection state as inputs.
 */
export const useListTableColumns = ({
	notify,
	selectAll,
	toggleSelectAll,
	selectedFiles,
	handleSelectFile,
	sortState,
	handleToggle,
	isSequencerActive,
	isBulkPending,
	getSummaries,
	bulkToggle,
	toggleCampaign,
	navigate,
	handleDownloadSelected,
	handleResetModal,
	handleArchiveModal,
	handleDeleteModal,
	handleDuplicateModal,
}: UseListTableColumnsParams): TableColumn[] => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`list.${key}`), [t]);
	const [runListNow] = useRunListNowMutation();

	const handleRunNow = useCallback(
		async (id: number) => {
			try {
				await runListNow(id).unwrap();
			} catch (error) {
				notify(translateRequestError(error));
			}
		},
		[runListNow, notify]
	);

	return useMemo(() => {
		const columns: TableColumn[] = [
			{
				title: "",
				uid: "select",
				width: "52px",
				svgIcon: selectAll ? SquareCheck : Square,
				onHeaderClick: () => {
					toggleSelectAll();
				},
				render: (row: List, index?: number) => (
					<div className="group flex items-center gap-2">
						<div
							className={classNames(
								"w-[24px] text-center font-medium text-table-header flex flex-row items-center justify-center gap-2",
								selectedFiles?.find((lf) => lf.id === row.id) ? "hidden" : "group-hover:hidden"
							)}
						>
							{typeof index !== "undefined" ? index + 1 : ""}
						</div>

						<Checkbox
							className={classNames(
								selectedFiles?.find((lf) => lf.id === row.id) ? "block" : "hidden group-hover:block"
							)}
							isChecked={!!selectedFiles?.find((lf) => lf.id === row.id)}
							name={"selected"}
							onChange={() => {
								handleSelectFile({ id: row.id, name: row.name, specialType: row.specialType });
							}}
						/>
					</div>
				),
			},
			{
				title: "",
				uid: "expand",
				width: "36px",
				isExpandToggle: true,
				render: () => <></>,
			},
			{
				title: ts("enrichment.title"),
				width: "210px",
				maxWidth: "210px",
				orderTitle: "status",
				uid: "status",
				orderStatus: sortState.sortBy === "status" ? sortState.sortOrder : 0,
				render: (row: List) => (
					<EnrichmentCell list={row} onRunNow={handleRunNow} onToggle={handleToggle} />
				),
			},
			{
				title: ts("status.campaignStatus"),
				width: "160px",
				orderTitle: "campaignStatus",
				orderStatus: sortState.sortBy === "campaignStatus" ? sortState.sortOrder : 0,
				uid: "campaignStatus",
				render: (row: List) => (
					<CampaignsBulkCell
						isUpdating={isBulkPending(row.id)}
						row={row}
						summaries={getSummaries(row.id)}
						onToggleAll={(next) => bulkToggle(row.id, next)}
					/>
				),
				renderSubRow: (sub: CampaignSummary | { loading: true }, parent: List) => (
					<CampaignStatusSubCell
						row={sub}
						onToggle={(campaign) => toggleCampaign(parent.id, campaign)}
					/>
				),
			},
			{
				title: ts("name"),
				minWidth: "240px",
				orderTitle: "name",
				orderStatus: sortState.sortBy === "name" ? sortState.sortOrder : 0,
				uid: "name",
				render: (row: List) => <ListNameCell isSequencerActive={isSequencerActive} list={row} />,
				renderSubRow: (sub: CampaignSummary | { loading: true }, parent: List) => (
					<CampaignNameSubCell
						row={sub}
						onSettings={(campaign) => navigate(buildCampaignBuilderPath(parent.id, campaign))}
					/>
				),
			},
			{
				title: ts("newLeads"),
				width: "112px",
				orderTitle: "newLeads",
				orderStatus: sortState.sortBy === "newLeads" ? sortState.sortOrder : 0,
				uid: "newLeads",
				render: (row: List) => (
					<div className="flex">
						{row.found && row?.found > 0 ? (
							<Chip className="bg-brand-light" color="blue" size="sm" value={`+${row?.found}`} />
						) : (
							""
						)}
					</div>
				),
				renderSubRow: (sub: CampaignSummary | { loading: true }) => (
					<CampaignMetricSubCell metric="contacted" row={sub} />
				),
			},
			{
				title: ts("assignedLeads"),
				width: "112px",
				orderTitle: "assignedLeads",
				orderStatus: sortState.sortBy === "assignedLeads" ? sortState.sortOrder : 0,
				uid: "assignedLeads",
				render: (row: List) => (
					<div className="flex">
						{row.assignedLeads && row.assignedLeads > 0 ? (
							<Chip
								className="bg-brand-light"
								color="blue"
								size="sm"
								value={`${row?.assignedLeads}`}
							/>
						) : (
							""
						)}
					</div>
				),
				renderSubRow: (sub: CampaignSummary | { loading: true }) => (
					<CampaignMetricSubCell metric="replied" row={sub} />
				),
			},
			{
				title: ts("lastAddedAt"),
				width: "140px",
				orderTitle: "lastAddedAt",
				orderStatus: sortState.sortBy === "lastAddedAt" ? sortState.sortOrder : 0,
				uid: "lastAddedAt",
				render: (row: List) => {
					return (
						<div className="flex">
							<div className="text-sm">
								{row?.lastAddedAt && format(new Date(row.lastAddedAt), "yyyy-MM-dd HH:mm")}
							</div>
						</div>
					);
				},
				renderSubRow: (sub: CampaignSummary | { loading: true }) => (
					<CampaignMetricSubCell metric="positive" row={sub} />
				),
			},
			{
				title: "",
				width: "56px",
				maxWidth: "56px",
				render: (row: List) => {
					const eligibility = archiveDeleteEligibility(row);
					const disabledReason = eligibility.allowed
						? undefined
						: t({ LIST_IS_PROCESSING: "errors.listIsProcessing" }[eligibility.reason]);

					let options: MenuItem[] = [
						{
							title: ts("editList"),
							onClick: () => {
								navigate(`/app/lists/create/settings/${row.id}`);
							},
						},
						...(isSequencerActive
							? [
									{
										title: ts("editCampaign"),
										onClick: () => {
											navigate(`/app/lists/create/copywriting/${row.id}`);
										},
									},
								]
							: []),
						{
							title: ts("goToAnalytics"),
							onClick: () => {
								navigate(`/app/analytics?listId=${row.id}`);
							},
						},
						{
							title: ts("download"),
							onClick: () => {
								handleDownloadSelected([{ id: row.id, name: row.name }]);
							},
						},
						{
							title: ts("resetList"),
							disabled: [ListProcessingStatus.PROCESSING].includes(row.processingStatus),
							onClick: () => {
								handleResetModal(row);
							},
						},
						{
							title: row.archived ? ts("unarchiveList") : ts("archiveList"),
							permissions: [PermissionRoles.LIST],
							// PREC-4537: the same decision the API makes, so a list whose run is in
							// flight cannot be archived from here (or by a direct call).
							disabled: !eligibility.allowed,
							disabledReason,
							onClick: () => {
								handleArchiveModal(row);
							},
						},
						{
							title: ts("deleteList"),
							permissions: [PermissionRoles.LIST],
							disabled: !eligibility.allowed,
							disabledReason,
							onClick: () => {
								handleDeleteModal(row);
							},
						},
						{
							title: ts("duplicate.title"),
							permissions: [PermissionRoles.LIST],
							onClick: () => {
								handleDuplicateModal(row);
							},
						},
					];

					if (row.specialType !== null) {
						options = options.filter(
							(option) =>
								option.title === ts("download") ||
								option.title === ts("editList") ||
								option.title === ts("goToAnalytics")
						);
					}

					return (
						<div className="flex justify-end gap-2">
							{/* Appends the flag-gated "Report a problem" entry (PREC-3937) after the
							    specialType trim above, so a special list keeps it too. */}
							<ReportProblemMenu
								options={options}
								prefill={{
									linkedEntity: { type: TicketLinkedEntityType.LIST, id: Number(row.id) },
								}}
								width="150"
							>
								<SvgIcon
									className="w-[20px] h-[20px] text-gray-700 cursor-pointer"
									svgIcon={ThreeDots}
								/>
							</ReportProblemMenu>
						</div>
					);
				},
			},
		];

		return columns.filter(
			(column) =>
				(isSequencerActive && column.uid === "campaignStatus") || column.uid !== "campaignStatus"
		);
	}, [
		selectAll,
		toggleSelectAll,
		selectedFiles,
		handleSelectFile,
		ts,
		t,
		sortState,
		handleToggle,
		handleRunNow,
		isBulkPending,
		getSummaries,
		bulkToggle,
		toggleCampaign,
		navigate,
		isSequencerActive,
		handleDownloadSelected,
		handleResetModal,
		handleArchiveModal,
		handleDeleteModal,
		handleDuplicateModal,
	]);
};
